// Split from djValueAdd.ts (I2). Shared local runtime helpers kept per-file for isolation.
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 44 — Client Preview / Approval Portal
// ============================================================================

/** The kind of project resource that can be exposed through a client preview/approval portal. */
export type DJSEMClientPortalResourceType =
  | "event_brief" | "must_play_list" | "do_not_play_list" | "brand_world_preview"
  | "visual_scene_preview" | "lighting_simulation_preview" | "asset_preview"
  | "intro_screen" | "technical_rider_preview" | "gig_pack_summary" | "release_campaign_preview";

/** An action a client can take from within a portal link. */
export type DJSEMClientPortalAction =
  | "view" | "comment" | "approve" | "request_changes"
  | "upload_logo" | "add_must_play" | "add_do_not_play" | "confirm_event_details";

/** Lifecycle status of a client portal link. */
export type DJSEMClientPortalStatus = "draft" | "active" | "expired" | "revoked";

/** A single resource surfaced to the client within a portal link, with its own visibility and approval state. */
export interface DJSEMClientPortalResource {
  id: string;
  resourceType: DJSEMClientPortalResourceType;
  resourceId: string;
  displayTitle: string;
  visibility: "visible" | "hidden";
  approvalRequired: boolean;
  status: "not_reviewed" | "approved" | "changes_requested" | "rejected";
  warnings: string[];
}

/** A shareable, tokenized link that grants a client scoped, time-limited access to a set of project resources. */
export interface DJSEMClientPortalLink {
  id: string;
  projectId: string;
  title: string;
  tokenHash: string;
  status: DJSEMClientPortalStatus;
  expiresAt?: string;
  allowedActions: DJSEMClientPortalAction[];
  visibleResources: DJSEMClientPortalResource[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  warnings: string[];
}

/** A track a client has requested (must-play or do-not-play) through the portal, pending review/matching. */
export interface DJSEMClientRequestedTrack {
  id: string;
  title: string;
  artist?: string;
  platformUrl?: string;
  notes?: string;
  status: "requested" | "accepted" | "rejected" | "matched" | "unmatched";
  matchedDjsemTrackId?: string;
  warnings: string[];
}

/** A single client submission (comment, approval, or track request) made against a portal link. */
export interface DJSEMClientPortalSubmission {
  id: string;
  portalLinkId: string;
  submittedByName?: string;
  action: DJSEMClientPortalAction;
  targetResourceId?: string;
  comment?: string;
  mustPlayTracks?: DJSEMClientRequestedTrack[];
  doNotPlayTracks?: DJSEMClientRequestedTrack[];
  createdAt: string;
  warnings: string[];
}

/** Input required to create a new {@link DJSEMClientPortalLink}. */
export interface CreateClientPortalLinkInput {
  projectId: string;
  title: string;
  tokenHash: string;
  expiresAt?: string;
  allowedActions: DJSEMClientPortalAction[];
  visibleResources: DJSEMClientPortalResource[];
  createdBy?: string;
}

/** Creates, resolves, and manages client preview/approval portal links and their submissions. */
export class DJSEMClientPortalService {
  /**
   * Creates a new active portal link for a project.
   * @param input - the link title, token hash, visible resources, and allowed actions.
   * @returns the newly created portal link.
   * @throws if `input.tokenHash` is shorter than 32 characters.
   */
  createPortalLink(input: CreateClientPortalLinkInput): DJSEMClientPortalLink {
    if (input.tokenHash.length < 32) throw new Error("Token hash must be >= 32 chars");
    return {
      id: `cpl_${_seq.next()}`, projectId: input.projectId, title: input.title,
      tokenHash: input.tokenHash, status: "active",
      expiresAt: input.expiresAt,
      allowedActions: input.allowedActions,
      visibleResources: input.visibleResources,
      createdBy: input.createdBy, createdAt: AT, updatedAt: AT, warnings: [],
    };
  }

  /**
   * Marks a portal link as revoked, immediately invalidating client access.
   * @param link - the portal link to revoke.
   * @returns a copy of the link with status set to `"revoked"`.
   */
  revokePortalLink(link: DJSEMClientPortalLink): DJSEMClientPortalLink {
    return { ...link, status: "revoked", updatedAt: AT };
  }

  /**
   * Resolves what a client should currently see for a portal link, honoring status and expiry.
   * @param link - the portal link being accessed.
   * @param now - the current timestamp to compare against `link.expiresAt`, if checking expiry.
   * @returns the visible resources and allowed actions, or `null` if the link is revoked, expired, still a draft, or past its `expiresAt`.
   */
  resolvePortalLink(link: DJSEMClientPortalLink, now?: string): { view: DJSEMClientPortalResource[]; actions: DJSEMClientPortalAction[] } | null {
    if (link.status === "revoked" || link.status === "expired") return null;
    if (link.status === "draft") return null;
    if (link.expiresAt && now && now > link.expiresAt) return null;
    const visible = link.visibleResources.filter(r => r.visibility === "visible");
    return { view: visible, actions: link.allowedActions };
  }

  /**
   * Records a client submission against a portal link if the action is permitted.
   * @param link - the portal link the submission is made against.
   * @param action - the action the client is performing.
   * @param payload - optional comment, requested tracks, and/or target resource for the submission.
   * @returns the created submission, or `null` if `action` is not in `link.allowedActions`.
   */
  submitAction(link: DJSEMClientPortalLink, action: DJSEMClientPortalAction, payload?: { comment?: string; mustPlayTracks?: DJSEMClientRequestedTrack[]; doNotPlayTracks?: DJSEMClientRequestedTrack[]; targetResourceId?: string; }): DJSEMClientPortalSubmission | null {
    if (!link.allowedActions.includes(action)) return null;
    return {
      id: `cps_${_seq.next()}`, portalLinkId: link.id,
      action, targetResourceId: payload?.targetResourceId,
      comment: payload?.comment,
      mustPlayTracks: payload?.mustPlayTracks,
      doNotPlayTracks: payload?.doNotPlayTracks,
      createdAt: AT, warnings: [],
    };
  }
}
